import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconEdit, IconX, IconArrowBackUp, IconAlertTriangle } from '@tabler/icons-react';
import { api } from '../../lib/api';
import {
  TIME_PROPOSALS_QUERY_KEY,
  type PortalProposalChanges,
  type PortalProposalValues,
  type TicketProposalChanges,
  type TicketProposalValues,
  type TimeProposal,
} from '../../lib/api';
import { formatProposalMinutes, formatProposalStart, REJECT_REASON_MIN_LENGTH } from '../../lib/timeProposals';
import { useBookingArticles } from '../../hooks/useBookingArticles';

const STATUS_LABEL: Record<TimeProposal['status'], { label: string; color: string }> = {
  OPEN: { label: 'Offen', color: 'yellow' },
  APPROVED: { label: 'Freigegeben', color: 'green' },
  REJECTED: { label: 'Verworfen', color: 'red' },
  CANCELLED: { label: 'Zurückgezogen', color: 'gray' },
};

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    // Präfix: Listen und Zähler (Kopfzeile, Startseite)
    queryClient.invalidateQueries({ queryKey: TIME_PROPOSALS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['weclapp-pending-work'] });
  };
}

/**
 * Ein Zeitvorschlag mit den Aktionen, die der Angemeldete hat: Freigeben, Bearbeiten (dann
 * freigeben), Verwerfen mit Grund, Zurückziehen (Absender). Die Rechte sagt der Server (`can*`).
 */
export function TimeProposalCard({ proposal, perspective }: { proposal: TimeProposal; perspective: 'received' | 'sent' | 'team' }) {
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const [editOpen, setEditOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const approve = useMutation({
    mutationFn: (changes?: TicketProposalChanges | PortalProposalChanges) => api.approveTimeProposal(proposal.id, changes),
    onSuccess: (res) => {
      setEditOpen(false);
      invalidate();
      notifications.show({
        title: 'Freigegeben',
        message: res.pending
          ? 'Die Zeit ist unter deinem Namen gespeichert und geht automatisch an weclapp, sobald es antwortet.'
          : 'Die Zeit ist unter deinem Namen gebucht.',
        color: 'green',
      });
    },
    onError: (e: Error) => {
      invalidate();
      notifications.show({ title: 'Nicht freigegeben', message: e.message, color: 'red' });
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.cancelTimeProposal(proposal.id),
    onSuccess: () => {
      invalidate();
      notifications.show({ title: 'Zurückgezogen', message: 'Der Vorschlag ist zurückgezogen.', color: 'gray' });
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  const status = STATUS_LABEL[proposal.status];
  const who =
    perspective === 'received'
      ? `Von ${proposal.sender.name ?? 'unbekannt'}`
      : perspective === 'sent'
        ? `An ${proposal.recipient.name ?? 'unbekannt'}`
        : `${proposal.sender.name ?? '?'} → ${proposal.recipient.name ?? '?'}`;
  const start = formatProposalStart(proposal.startAt);
  const busy = approve.isPending || cancel.isPending;

  return (
    <Card withBorder padding="sm" radius="md">
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <div style={{ minWidth: 0 }}>
            <Group gap={6} wrap="wrap">
              <Text size="sm" fw={600}>
                {who}
              </Text>
              <Badge size="sm" variant="light" color="grape">
                {formatProposalMinutes(proposal.minutes)}
              </Badge>
              {start && (
                <Text size="xs" c="dimmed">
                  {start}
                </Text>
              )}
            </Group>
            {proposal.ticket ? (
              <Anchor
                size="xs"
                onClick={() => navigate(proposal.ticket?.number ? `/t/${encodeURIComponent(proposal.ticket.number)}` : '/tickets')}
              >
                {proposal.ticket.number ?? 'Ticket'}
                {proposal.ticket.subject ? ` · ${proposal.ticket.subject}` : ''}
              </Anchor>
            ) : (
              <Text size="xs" c="dimmed">
                Zeitbuchung ohne Ticket{proposal.customer?.name ? ` · ${proposal.customer.name}` : ''} — die Freigabe legt das Ticket an
              </Text>
            )}
          </div>
          <Badge size="sm" color={status.color} variant="light" style={{ flexShrink: 0 }}>
            {status.label}
          </Badge>
        </Group>

        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} lineClamp={4}>
          {proposal.comment}
        </Text>

        {proposal.status === 'REJECTED' && proposal.rejectReason && (
          <Text size="xs" c="red">
            Verworfen{proposal.decidedBy?.name ? ` von ${proposal.decidedBy.name}` : ''}: {proposal.rejectReason}
          </Text>
        )}
        {proposal.status === 'APPROVED' && (
          <Text size="xs" c="dimmed">
            Freigegeben{proposal.decidedBy?.name ? ` von ${proposal.decidedBy.name}` : ''}
            {proposal.edited ? ' — mit geänderten Werten' : ''}
            {proposal.bookingMessage ? ` · ${proposal.bookingMessage}` : ''}
          </Text>
        )}
        {proposal.status === 'OPEN' && proposal.bookingMessage && (
          <Alert color="orange" variant="light" p="xs" icon={<IconAlertTriangle size={14} />}>
            <Text size="xs">Letzter Freigabeversuch: {proposal.bookingMessage}</Text>
          </Alert>
        )}

        {proposal.status === 'OPEN' && (proposal.canApprove || proposal.canReject || proposal.canCancel) && (
          <Group gap="xs" mt={4}>
            {proposal.canApprove && (
              <>
                <Button size="xs" color="green" leftSection={<IconCheck size={14} />} loading={approve.isPending} disabled={busy} onClick={() => approve.mutate(undefined)}>
                  Freigeben
                </Button>
                <Button size="xs" variant="light" leftSection={<IconEdit size={14} />} disabled={busy} onClick={() => setEditOpen(true)}>
                  Bearbeiten
                </Button>
              </>
            )}
            {proposal.canReject && (
              <Button size="xs" variant="light" color="red" leftSection={<IconX size={14} />} disabled={busy} onClick={() => setRejectOpen(true)}>
                Verwerfen
              </Button>
            )}
            {proposal.canCancel && (
              <Button size="xs" variant="subtle" color="gray" leftSection={<IconArrowBackUp size={14} />} loading={cancel.isPending} disabled={busy} onClick={() => cancel.mutate()}>
                Zurückziehen
              </Button>
            )}
          </Group>
        )}
      </Stack>

      {editOpen && (
        <EditProposalModal
          proposal={proposal}
          loading={approve.isPending}
          onClose={() => setEditOpen(false)}
          onApprove={(changes) => approve.mutate(changes)}
        />
      )}
      {rejectOpen && <RejectProposalModal proposal={proposal} onClose={() => setRejectOpen(false)} onDone={invalidate} />}
    </Card>
  );
}

function RejectProposalModal({ proposal, onClose, onDone }: { proposal: TimeProposal; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const tooShort = reason.trim().length < REJECT_REASON_MIN_LENGTH;
  const reject = useMutation({
    mutationFn: () => api.rejectTimeProposal(proposal.id, reason.trim()),
    onSuccess: () => {
      onDone();
      onClose();
      notifications.show({ title: 'Verworfen', message: `${proposal.sender.name ?? 'Der Absender'} bekommt den Grund per Push.`, color: 'gray' });
    },
    onError: (e: Error) => notifications.show({ title: 'Nicht verworfen', message: e.message, color: 'red' }),
  });
  return (
    <Modal opened onClose={onClose} title="Zeitvorschlag verwerfen" centered>
      <Stack gap="sm">
        <Text size="sm">
          Es wird nichts gebucht. {proposal.sender.name ?? 'Der Absender'} bekommt eine Push-Nachricht mit deinem Grund.
        </Text>
        <Textarea
          label="Grund"
          placeholder="z. B. War an dem Tag nicht beim Kunden / doppelt erfasst"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          autosize
          minRows={2}
          required
          error={reason.length > 0 && tooShort ? `Mindestens ${REJECT_REASON_MIN_LENGTH} Zeichen` : undefined}
          data-autofocus
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Abbrechen
          </Button>
          <Button color="red" disabled={tooShort} loading={reject.isPending} onClick={() => reject.mutate()}>
            Verwerfen
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Ms → Wert für `<input type="datetime-local">` (Uhr des Geräts, wie das Buchungsformular im Cockpit). */
function toLocalInput(ms: number | null): string {
  if (ms == null) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function EditProposalModal({
  proposal,
  loading,
  onClose,
  onApprove,
}: {
  proposal: TimeProposal;
  loading: boolean;
  onClose: () => void;
  onApprove: (changes: TicketProposalChanges | PortalProposalChanges) => void;
}) {
  return (
    <Modal opened onClose={onClose} title="Bearbeiten und freigeben" centered size="lg">
      <Text size="sm" c="dimmed" mb="sm">
        Gebucht wird unter deinem Namen, mit den Werten unten. {proposal.sender.name ?? 'Der Absender'} erfährt, dass du
        sie geändert hast.
      </Text>
      {proposal.kind === 'PORTAL' ? (
        <PortalEditForm proposal={proposal} loading={loading} onClose={onClose} onApprove={onApprove} />
      ) : (
        <TicketEditForm proposal={proposal} loading={loading} onClose={onClose} onApprove={onApprove} />
      )}
    </Modal>
  );
}

function TicketEditForm({
  proposal,
  loading,
  onClose,
  onApprove,
}: {
  proposal: TimeProposal;
  loading: boolean;
  onClose: () => void;
  onApprove: (changes: TicketProposalChanges) => void;
}) {
  const v = proposal.payload as TicketProposalValues;
  const articles = useBookingArticles();
  const [minutes, setMinutes] = useState<number>(Math.round(v.hours * 60));
  const [start, setStart] = useState(toLocalInput(v.startDate));
  const [comment, setComment] = useState(v.comment);
  const [articleId, setArticleId] = useState<string | null>(v.articleId ?? articles.defaultArticleId);

  const submit = () => {
    const changes: TicketProposalChanges = {};
    const hours = Math.round((minutes / 60) * 10000) / 10000;
    if (Math.abs(hours - v.hours) > 1e-6) changes.hours = hours;
    const startMs = start ? new Date(start).getTime() : null;
    if (startMs !== v.startDate && !(startMs == null && v.startDate == null)) changes.startDate = startMs;
    if (comment.trim() !== v.comment) changes.comment = comment.trim();
    const art = articleId && articleId !== articles.defaultArticleId ? articleId : null;
    if (art !== (v.articleId ?? null)) changes.articleId = art;
    onApprove(changes);
  };

  return (
    <Stack gap="sm">
      <Group grow>
        <NumberInput label="Dauer (Minuten)" min={1} max={24 * 60} value={minutes} onChange={(x) => setMinutes(Number(x) || 0)} />
        <TextInput label="Beginn" type="datetime-local" value={start} onChange={(e) => setStart(e.currentTarget.value)} />
      </Group>
      {articles.hasChoice && (
        <Select
          label="Leistungsart"
          data={articles.options.map((o) => ({ value: o.id, label: o.name }))}
          value={articleId}
          onChange={setArticleId}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true, zIndex: 1100 }}
        />
      )}
      <Textarea
        label="Tätigkeit (Text des Leistungsnachweises)"
        value={comment}
        onChange={(e) => setComment(e.currentTarget.value)}
        autosize
        minRows={3}
        required
      />
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Abbrechen
        </Button>
        <Button color="green" loading={loading} disabled={minutes <= 0 || comment.trim().length === 0} onClick={submit}>
          Speichern und freigeben
        </Button>
      </Group>
    </Stack>
  );
}

const BILLING_OPTIONS = [
  { value: 'billable', label: 'Abrechenbar' },
  { value: 'internal', label: 'Intern' },
  { value: 'goodwill', label: 'Kulanz' },
  { value: 'non-billable', label: 'Nicht abrechenbar' },
];

function PortalEditForm({
  proposal,
  loading,
  onClose,
  onApprove,
}: {
  proposal: TimeProposal;
  loading: boolean;
  onClose: () => void;
  onApprove: (changes: PortalProposalChanges) => void;
}) {
  const v = proposal.payload as PortalProposalValues;
  const [summary, setSummary] = useState(v.summary);
  const [description, setDescription] = useState(v.description ?? '');
  const [date, setDate] = useState(v.date ?? '');
  const [startTime, setStartTime] = useState(v.startTime ?? '');
  const [endTime, setEndTime] = useState(v.endTime ?? '');
  const [breakMinutes, setBreakMinutes] = useState<number>(v.breakMinutes ?? 0);
  const [minutes, setMinutes] = useState<number>(v.minutes);
  const [billingType, setBillingType] = useState<string>(v.billingType ?? 'billable');
  const [billingReason, setBillingReason] = useState(v.billingReason ?? '');

  // Wie auf der Seite Zeitbuchung: Beginn/Ende/Pause ergeben die Dauer.
  const recalc = (s: string, e: string, b: number) => {
    if (!s || !e) return;
    let diff = timeToMinutes(e) - timeToMinutes(s);
    if (diff < 0) diff += 24 * 60;
    setMinutes(Math.max(1, diff - Math.max(0, b)));
  };

  const needsReason = billingType !== 'billable';
  const submit = () => {
    const changes: PortalProposalChanges = {};
    if (summary.trim() !== v.summary) changes.summary = summary.trim();
    if (description.trim() !== (v.description ?? '')) changes.description = description.trim();
    if (date && date !== v.date) changes.date = date;
    if (startTime && startTime !== v.startTime) changes.startTime = startTime;
    if (endTime && endTime !== v.endTime) changes.endTime = endTime;
    if (breakMinutes !== (v.breakMinutes ?? 0)) changes.breakMinutes = breakMinutes;
    if (minutes !== v.minutes) changes.minutes = minutes;
    if (billingType !== (v.billingType ?? 'billable')) {
      changes.billingType = billingType as PortalProposalChanges['billingType'];
      // Teilbuchung gehört zur alten Abrechnungsart — beim Wechsel neu entscheiden lassen.
      changes.billingPercent = null;
    }
    if (needsReason && billingReason.trim() !== (v.billingReason ?? '')) changes.billingReason = billingReason.trim();
    onApprove(changes);
  };

  return (
    <Stack gap="sm">
      <TextInput label="Kurztext" value={summary} onChange={(e) => setSummary(e.currentTarget.value)} required />
      <Textarea label="Beschreibung" value={description} onChange={(e) => setDescription(e.currentTarget.value)} autosize minRows={2} />
      <Group grow>
        <TextInput label="Datum" type="date" value={date} onChange={(e) => setDate(e.currentTarget.value)} />
        <TextInput
          label="Beginn"
          type="time"
          value={startTime}
          onChange={(e) => {
            const __val = e.currentTarget.value;
            setStartTime(__val);
            recalc(__val, endTime, breakMinutes);
          }}
        />
        <TextInput
          label="Ende"
          type="time"
          value={endTime}
          onChange={(e) => {
            const __val = e.currentTarget.value;
            setEndTime(__val);
            recalc(startTime, __val, breakMinutes);
          }}
        />
      </Group>
      <Group grow>
        <NumberInput
          label="Pause (Minuten)"
          min={0}
          max={480}
          value={breakMinutes}
          onChange={(x) => {
            const b = Number(x) || 0;
            setBreakMinutes(b);
            recalc(startTime, endTime, b);
          }}
        />
        <NumberInput label="Dauer (Minuten)" min={1} max={24 * 60} value={minutes} onChange={(x) => setMinutes(Number(x) || 0)} />
      </Group>
      <Select
        label="Abrechnungsart"
        data={BILLING_OPTIONS}
        value={billingType}
        onChange={(x) => setBillingType(x ?? 'billable')}
        allowDeselect={false}
        comboboxProps={{ withinPortal: true, zIndex: 1100 }}
      />
      {needsReason && (
        <Textarea label="Begründung (intern)" value={billingReason} onChange={(e) => setBillingReason(e.currentTarget.value)} autosize minRows={2} required />
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          color="green"
          loading={loading}
          disabled={minutes <= 0 || summary.trim().length === 0 || (needsReason && billingReason.trim().length === 0)}
          onClick={submit}
        >
          Speichern und freigeben
        </Button>
      </Group>
    </Stack>
  );
}
